import { mapObject } from './helpers/mapObject'

/**
 * Extracts the return type of a function, e.g.
 * type F = () => string
 * FunctionReturnType<F> will equal string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionReturnType<T> = T extends (...args: any[]) => infer R ? R : never

/**
 * Maps an object of selectors to an object of selectors of other state, e.g.
 * type F = { getName: (state: LocalState) => string }
 * MapperSelector<F, GlobalState> = { getName: (state: GlobalState) => string }
 */
type LiftedSelector<TSelector, TGlobalState> = {
  [P in keyof TSelector]: (state: TGlobalState) => FunctionReturnType<TSelector[P]>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectorType = Record<string, (...args: any[]) => any>

export function liftSelectors<GlobalState, LocalState, Selectors extends SelectorType>(
  toLocalState: (state: GlobalState) => LocalState,
  selectors: Selectors
): LiftedSelector<Selectors, GlobalState> {
  function mapSelector<ReturnValue>(selector: (state: LocalState) => ReturnValue) {
    // eslint-disable-next-line func-names
    return function (state: GlobalState) {
      const localState: LocalState = toLocalState(state)
      return selector(localState)
    }
  }

  return mapObject(selectors, mapSelector) as LiftedSelector<Selectors, GlobalState>
}

/**
 * Maps an object of selectors to an object of selectors of other state, e.g.
 * type F = { getName: (state: LocalState) => string }
 * MapperSelector<F, GlobalState> = { getName: (state: GlobalState) => string }
 */
type LiftedSelectorWithArgument<TSelector, TGlobalState, Argument> = {
  [P in keyof TSelector]: (state: TGlobalState) => (x: Argument) => FunctionReturnType<TSelector[P]>
}

export function liftSelectorsWithArgument<GlobalState, LocalState, Selectors extends SelectorType, Argument>(
  toLocalState: (state: GlobalState, x: Argument) => LocalState,
  selectors: Selectors
): LiftedSelectorWithArgument<Selectors, GlobalState, Argument> {
  function mapSelector<ReturnValue>(selector: (state: LocalState) => ReturnValue) {
    // eslint-disable-next-line func-names
    return function (state: GlobalState) {
      // eslint-disable-next-line func-names
      return function (x: Argument) {
        const localState: LocalState = toLocalState(state, x)
        return selector(localState)
      }
    }
  }

  return mapObject(selectors, mapSelector) as LiftedSelectorWithArgument<Selectors, GlobalState, Argument>
}
